import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';

import { DeviceUiPage, DeviceUiRoute, DeviceUiSharedDeviceBehaviorPlugin } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import { CoverageMode, DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import { DeviceCoverageDeviceBehaviorPlugin } from '@shure/shared/angular/features/device-coverage/data-access';
import { CoverageImage, CoverageImageProvider } from '@shure/shared/angular/features/device-coverage/ui';
import { LightsSettingsSupportType } from '@shure/shared/angular/features/device-lights-settings/data-access';
import { ConfirmDialogData, ShowIcon } from '@shure/shared/angular/ui/components/dialogs/confirm';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class Dca901DeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiSharedDeviceBehaviorPlugin,
		DeviceCoverageDeviceBehaviorPlugin,
		CoverageImageProvider
{
	public readonly models = [DeviceModel.DCA901];

	constructor(protected readonly transloco: TranslocoService) {}

	public getAvailablePages$(
		_model: DeviceModel,
		_options?: { virtual?: boolean | undefined; autoCoverageMode?: boolean | undefined } | undefined
	): Observable<DeviceUiPage[] | undefined> {
		return of([
			{
				route: DeviceUiRoute.CoverageDca901,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.coverage')
			},
			{
				route: DeviceUiRoute.ChannelsDca901,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.channels')
			},
			{
				route: DeviceUiRoute.Presets,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.presets')
			},
			{
				route: DeviceUiRoute.Settings,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.settings')
			}
		]);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public getCoverageImage(_model: DeviceModel, micView: any, micOrientation: any): CoverageImage {
		if (micView) {
			return {
				live: `device-dca901-${micView}-${micOrientation}.svg`,
				virtual: `device-dca901-${micView}-${micOrientation}-line.svg`,
				isImageRound: false
			};
		}

		return {
			live: 'device-dca901-back.svg',
			virtual: 'device-dca901-back-line.svg',
			isImageRound: true
		};
	}

	public getDefaultRoute(_model: DeviceModel, _coverageMode?: CoverageMode | undefined): DeviceUiRoute | undefined {
		return DeviceUiRoute.CoverageDca901;
	}

	public getChannelsRoute(): DeviceUiRoute {
		return DeviceUiRoute.ChannelsDca901;
	}

	public getLightsSettingsSupportType(): LightsSettingsSupportType {
		return LightsSettingsSupportType.SupportedLegacy;
	}

	public showNoiseReductionIntensityLevel(): boolean {
		return true;
	}

	public usePostGateValues(): boolean {
		return false;
	}

	public supportsChannels(): boolean {
		return true;
	}

	public supportsDeviceLocalRotation(): boolean {
		return true;
	}

	public getEqualizerBypassConfirmDialog(): ConfirmDialogData {
		return {
			title: this.transloco.translate('scDevUiSharedDevBehavior.dsp.bypass-peq-confirm-dialog-title'),
			body: this.transloco.translate('scDevUiSharedDevBehavior.dsp.bypass-peq-confirm-dialog-body'),
			cancelLabel: this.transloco.translate('scDevUiSharedDevBehavior.common.buttons.cancel'),
			confirmLabel: this.transloco.translate(
				'scDevUiSharedDevBehavior.dsp.bypass-peq-confirm-dialog-confirm-button'
			),
			doNotShowDialogId: 'turnoff-bypass-eq',
			showIcon: ShowIcon.Warning
		};
	}
}
